import {
  PopoverElementKind,
  useHoverCardPosition,
} from '../hooks/useHoverCardPosition'
import { useLightMode } from '@/hooks/useLightMode'

import { Box, Popover, PopoverArrow, PopoverContent } from '@chakra-ui/react'

import { DarkProvider } from '@hivewatch/dark'

import { PopoverTrigger } from '@/components/ui'

export interface DarkFloorPlanPopoverIProps {
  children: React.ReactNode
  elementId: string
  kind?: PopoverElementKind
  isHovering: boolean
  forceCloseSignal?: number
}

/**
 * The client rect is in viewport coordinates while the trigger is positioned
 * against an ancestor these offsets compensate for by hand. They are not design
 * values — they are the legacy anchoring, kept identical so the dark card lands
 * where the legacy one does. Owned by the map layer; don't tune them here.
 *
 * Known bug (pre-existing, not this file's to fix): TRIGGER_OFFSET_X assumes
 * a collapsed side nav (60px) and is off by the expanded width (240px) when
 * it's open, landing the card 180px right of its marker. TRIGGER_OFFSET_Y is
 * correct.
 */
const TRIGGER_OFFSET_X = -60
const TRIGGER_OFFSET_Y = -65

/**
 * Card width per anchor kind. The tailgate card is narrower because it holds
 * two device rows and copy, while the device card has to fit a camera feed.
 */
const POPOVER_WIDTH: Record<PopoverElementKind, string> = {
  device: '400px',
  tailgate: '320px',
}

/**
 * Dark twin of FloorPlanPopover. The anchoring — the open delay, the one-shot
 * client rect and the trigger offsets above — is a deliberate verbatim copy of
 * the legacy component, so this card appears exactly where the legacy one does.
 * Only the surface is rebranded; the positioning is owned by the map layer and
 * is out of scope here.
 */
export const DarkFloorPlanPopover = ({
  isHovering = false,
  elementId,
  children,
  kind,
  forceCloseSignal,
}: DarkFloorPlanPopoverIProps) => {
  const colorScheme = useLightMode()

  const {
    triggerPosition,
    isOpen,
    onContentMouseEnter,
    onContentMouseLeave,
    onPopoverClose,
  } = useHoverCardPosition({ elementId, isHovering, kind, forceCloseSignal })

  const { x, y, width, height } = triggerPosition
  return (
    <Popover
      autoFocus={false}
      closeOnBlur={true}
      isLazy
      isOpen={isOpen}
      offset={[8, 8]}
      onClose={onPopoverClose}
      returnFocusOnClose={false}
      // Not 'hover': Chakra's own hover trigger schedules an unconditional
      // close on mouseleave that can't be cancelled, which used to kill a
      // card re-opened right after. Open/close is fully driven by the hook.
      // autoFocus/returnFocusOnClose default true under Chakra's default
      // 'click' trigger, which would steal focus from e.g. a search input
      // whenever the pointer happens to rest on a marker long enough to
      // open the card.
    >
      <PopoverTrigger>
        {/* Anchor only, for Popper to position against — pointerEvents='none'
            so a stale rect can never intercept a click on the canvas. */}
        <Box
          height={height}
          left={x + TRIGGER_OFFSET_X}
          pointerEvents='none'
          pos='absolute'
          top={y + TRIGGER_OFFSET_Y}
          width={width}
        />
      </PopoverTrigger>
      {/* The surface is Chakra v1 and stays outside DarkProvider so its tokens
          resolve against the v1 theme; DarkProvider wraps only the content.
          Square corners and the subtle border mirror the dark package's Card,
          which sets no radius — radius/none is the design library's default for
          every standard product surface, and the chamfer is modal-only. */}
      <PopoverContent
        bg={`bg.card-elevated.${colorScheme}`}
        border='1px solid'
        borderColor={`border.subtle.${colorScheme}`}
        borderRadius='none'
        onMouseEnter={onContentMouseEnter}
        onMouseLeave={onContentMouseLeave}
        shadow='xl'
        width={POPOVER_WIDTH[kind ?? 'device']}
      >
        <PopoverArrow bg={`bg.card-elevated.${colorScheme}`} />
        <DarkProvider colorScheme={colorScheme}>{children}</DarkProvider>
      </PopoverContent>
    </Popover>
  )
}
